import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Send } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUserProfile } from "@/api/users-query";
import { useCreateWithdrawal, useViewWallet } from "@/api/wallet-query";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { NumInputSeparated } from "@/components/ui/num-input-separated";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, truncateText } from "@/lib/utils";
import { PaymentProvider, TransactionType } from "@/types/banks";
import { zodResolver } from "@hookform/resolvers/zod";

const withdrawSchema = z.object({
	amount: z.coerce.string({
		required_error: "Amount is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Amount must be a number greater than 0"
	}),
	bankId: z.string().nonempty("Bank is required"),
	description: z.string().nonempty("Description is required"),
	paymentProvider: z.string().optional(),
	pin: z.string().min(6, "Pin is required")
});

export function Withdraw() {
	const [open, setOpen] = useState(false);
	const [step, setStep] = useState<"withdraw" | "pin">("withdraw");
	const withdrawForm = useForm<z.infer<typeof withdrawSchema>>({
		resolver: zodResolver(withdrawSchema),
		defaultValues: {
			amount: "",
			description: "",
			bankId: "",
			paymentProvider: "paystack",
			pin: ""
		},
		mode: "onChange",
		reValidateMode: "onChange"
	});
	const withdrawQuery = useCreateWithdrawal();
	const handleBankSubmit = async (v: z.infer<typeof withdrawSchema>) => {
		await withdrawQuery.mutateAsync({
			amount: Number(v.amount),
			bankId: v.bankId,
			pin: v.pin,
			description: TransactionType.WITHDRAW_FUNDS,
			paymentProvider: PaymentProvider.PAYSTACK
		});
	};

	const walletQuery = useViewWallet();

	const userProfile = useUserProfile();

	useEffect(() => {
		if (withdrawQuery.isSuccess)
			setOpen(false);
	}, [open, withdrawQuery.isSuccess]);

	const handleClose = () => {
		setOpen(prev => !prev);
		withdrawForm.reset();
		setStep("withdraw");
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogTrigger asChild>
				<Button className="gap-2 border border-neutral-300" variant="secondary">
					<Send className="size-4" />
					Withdraw
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Withdraw</DialogTitle>
					<DialogDescription>

						{`Current balance: ${formatCurrency(Number(walletQuery.data?.data.withdrawableBalance), "NGN")}`}
					</DialogDescription>
					<DialogDescription>
						Make transfer from your Intasida wallet to any bank of your choice
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<Form {...withdrawForm}>
						<form onSubmit={withdrawForm.handleSubmit(handleBankSubmit)} className="space-y-4">
							<div className="flex flex-col items-start justify-center space-y-2">
								{step === "withdraw" && (
									<>
										<FormField
											control={withdrawForm.control}
											name="bankId"
											render={({ field }) => (
												<FormItem
													className="w-full"
												>
													<FormLabel>Select Bank</FormLabel>
													<FormControl>
														<Select
															defaultValue="total"
															value={field.value}
															onValueChange={(value) => {
																field.onChange(value);
																withdrawForm.setValue("bankId", value);
															}}
														>
															<SelectTrigger className="h-14">
																<SelectValue placeholder="Select an account" />
															</SelectTrigger>
															<SelectContent>
																{
																	userProfile.data?.bankAccounts.length === 0
																		? <div className="p-2">No bank accounts added</div>
																		: userProfile.data?.bankAccounts.map((account) => {
																			return (
																				<SelectItem key={account.id} value={account.id}>{truncateText(`${account.accountNumber} - ${account.bank.bankName}`, 40)}</SelectItem>
																			);
																		})

																}
															</SelectContent>
														</Select>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={withdrawForm.control}
											name="amount"
											render={({ field }) => (
												<FormItem
													className="w-full"
												>
													<FormLabel>Amount</FormLabel>
													<FormControl>
														<NumInputSeparated
															currency="NGN"
															aria-label="Amount"
															value={field.value ?? ""}
															onChange={field.onChange}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={withdrawForm.control}
											name="description"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel>Remark</FormLabel>
													<FormControl>
														<Input
															className="h-14"
															placeholder="Enter Remark"
															{...field}
														/>

													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</>
								)}

								{step === "pin" && (
									<>
										<FormField
											name="pin"
											control={withdrawForm.control}
											render={({ field }) => (
												<FormItem className="border-0">
													<FormControl>
														<InputOTP maxLength={6} {...field} pattern={REGEXP_ONLY_DIGITS}>
															<InputOTPGroup className="w-full justify-center gap-4 p-0">
																<InputOTPSlot index={0} className="border rounded  size-12" />
																<InputOTPSlot index={1} className="border rounded size-12" />
																<InputOTPSlot index={2} className="border rounded size-12" />
																<InputOTPSlot index={3} className="border rounded size-12" />
																<InputOTPSlot index={4} className="border rounded size-12" />
																<InputOTPSlot index={5} className="border rounded size-12" />
															</InputOTPGroup>
														</InputOTP>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</>
								)}
							</div>
						</form>
					</Form>
				</div>
				<DialogFooter>
					{
						step === "pin" && (
							<Button
								variant="outline"
								onClick={() => setStep("withdraw")}
							>
								Back
							</Button>
						)
					}
					<Button
						className="w-full"
						type="submit"
						disabled={withdrawQuery.isPending}
						onClick={async () => {
							if (step === "withdraw") {
								const isValid = await withdrawForm.trigger(["amount", "bankId", "description"]);
								if (!isValid)
									return;

								const amount = withdrawForm.getValues("amount");

								if (Number(amount) > Number(walletQuery?.data?.data.withdrawableBalance)) {
									withdrawForm.setError("amount", {
										message: "Amount is greater than the available balance"
									});
									return;
								}
								setStep("pin");
							}
							else {
								withdrawForm.handleSubmit(handleBankSubmit)();
							}
						}}
					>
						{
							step === "withdraw"
								? "Continue"
								: withdrawQuery.isPending ? "Processing..." : "Withdraw"
						}
					</Button>

				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
